import React, { useEffect, useRef } from 'react'
import { formatCurrency } from 'utils/currencyFormatter'
import { trackEvent } from 'utils/analytics'
import { couponSteadyVisible, welcomeCouponVisible } from 'utils/welcomeCouponVisibility'

// Always-visible welcome-coupon pill for logged-out visitors. Rendered
// `position: fixed` at `z-[10000]` (above every modal, lightbox, and flash layer
// a logged-out visitor can reach). Position is responsive: mobile `top-2` (very
// top center, width-constrained to clear the modal corner close/back/share
// controls — it MAY overlap the centered title label, never a control) /
// desktop `lg:top-20` (below the modal header band). It is NOT dismissible.
// It is suppressed ONLY while its own login modal is open (see `suppressed`);
// it stays visible over all other modals. Shown ONLY to logged-out visitors.
export default function WelcomeCouponBanner({ team, currentUser, teamSlug, translations, onClaim, suppressed = false }) {
  const amount = Number(team?.welcome_coupon_amount)
  const currency = team?.welcome_coupon_currency || 'TWD'

  const enabled = team?.welcome_coupon_enabled
  // Steady-state visibility excludes `suppressed` so the impression effect does
  // not re-fire each time the login modal opens/closes.
  const steadyVisible = couponSteadyVisible({ enabled, amount, currentUser })
  const visible = welcomeCouponVisible({ enabled, amount, currentUser, suppressed })

  // Fire the impression once per page view, regardless of suppression toggles.
  const impressionFired = useRef(false)
  useEffect(() => {
    if (steadyVisible && !impressionFired.current) {
      impressionFired.current = true
      trackEvent('welcome_coupon_banner_impression', { team_slug: teamSlug })
    }
  }, [steadyVisible, teamSlug])

  const label = (translations?.welcomeCoupon?.cta || '')
    .replace('%{amount}', formatCurrency(amount, currency))

  // Hide entirely if not visible OR if the cta resolved to empty (missing
  // translation) — never ship a blank, unlabeled clickable pill.
  if (!visible || !label.trim()) return null

  const handleClaim = async () => {
    trackEvent('welcome_coupon_cta_click', { team_slug: teamSlug })
    try {
      await fetch(`/api/teams/${team.id}/welcome_coupon_intent`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      })
    } catch (e) {
      // Non-blocking: proceed to registration even if intent marking fails.
    }
    onClaim?.()
  }

  return (
    <div
      role="region"
      aria-label={label}
      className="fixed top-2 lg:top-20 left-1/2 -translate-x-1/2 z-[10000] px-4 max-w-[calc(100vw-7rem)] lg:max-w-full"
    >
      <button
        type="button"
        onClick={handleClaim}
        className="flex items-center gap-2 max-w-full rounded-full px-4 py-1.5 shadow-md text-white text-xs lg:text-sm font-medium hover:opacity-90 transition-opacity"
        style={{ backgroundColor: '#3366FF' }}
      >
        <span aria-hidden="true">🎁</span>
        <span className="truncate min-w-0">{label}</span>
      </button>
    </div>
  )
}
